import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { NavLink, Stack } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';

import type { LanguageSelectorContent, LanguageSelectorItem } from './language-selector-content.types';

/**
 * `LanguageSelector` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy list variant (`libs/sitecore/components/content/src/LanguageSelector/
 * LanguageSelector.tsx`): on desktop, only the *inactive* language(s) show, as primary links; below
 * `lg`, every language shows, with the active one marked current, as tertiary links.
 *
 * Known simplification: the legacy `RenderVariant: 'Dropdown'` swaps in a hand-styled popover
 * (`LangaugeSelectorDropdown`, built from vanilla-extract CSS and `useApplication`'s `searchParams`)
 * that lives in `lib:sitecore-components` — unreachable from this lane's module boundary (`nx.json`),
 * and its hand-rolled CSS approach is itself disallowed here (`dxp-rules/no-inline-css`). Every render
 * variant falls back to the same list rendering rather than dropping the language switcher entirely.
 *
 * Known simplification: see `language-selector-content.types.ts` — no Sanity content source for the
 * language list exists yet, so this renders nothing on `?_cms=sanity` until one does.
 *
 * On `?_cms=sitecore`, the language list arrives as the content object's own index-keyed values rather
 * than under a `languages` field — see `language-selector-content.types.ts` for why — so that's
 * reconstructed here via `Object.values`, filtering to values that are actually item-shaped.
 */
const isLanguageItem = (value: unknown): value is LanguageSelectorItem =>
  typeof value === 'object' && value !== null && typeof (value as LanguageSelectorItem).url === 'string';

const LanguageSelector: FC = () => {
  const content = useContent<LanguageSelectorContent>();
  const isDesktop = useMediaQuery('lg');
  const languages = content.languages ?? Object.values(content).filter(isLanguageItem);

  if (languages.length === 0) return null;

  return (
    <Stack direction="row" gap="4">
      {languages
        .filter(language => (isDesktop ? !language.active : true))
        .map(language => (
          <NavLink
            href={language.url}
            key={language.language}
            isCurrent={isDesktop ? false : language.active}
            variant={isDesktop ? 'primary' : 'tertiary'}>
            {language.languageDisplayName}
          </NavLink>
        ))}
    </Stack>
  );
};

export default LanguageSelector;
